import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface DeviceRow {
  readonly deviceId: string;
  readonly deviceIdentifier: string;
  readonly deviceName: string | null;
  readonly platform: string;
  readonly osVersion: string | null;
  readonly appVersion: string | null;
  readonly manufacturer: string | null;
  readonly model: string | null;
  readonly status: string;
  readonly lastSeenAt: string | null;
}

/**
 * M22 L22.02 — Mobile Device Management.
 * Lists the device registry (t281_device_master) via the M22 platform API and
 * surfaces the guarded lifecycle write actions — approve pending enrolments and
 * suspend / disable / revoke active devices (DEVICE_APPROVE / DEVICE_BLOCK /
 * DEVICE_REVOKE enforced server-side by IamAuthzGuard).
 */
export function M22DeviceManagementPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<DeviceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    return apiRequest<{ data: DeviceRow[] }>('/v1/mobile/devices', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: DeviceRow[] }>('/v1/mobile/devices', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const runAction = useCallback(
    async (deviceId: string, action: 'approve' | 'suspend' | 'disable' | 'revoke') => {
      setError(null);
      setNotice(null);
      let body: { reason: string } | undefined;
      if (action !== 'approve') {
        const reason = window.prompt(t('m22.deviceManagement.reasonPrompt', { action }));
        if (reason === null) return;
        body = { reason };
      }
      setBusy(`${action}:${deviceId}`);
      try {
        await apiRequest(`/v1/mobile/devices/${deviceId}/${action}`, {
          method: 'POST',
          token,
          ...(body ? { body } : {}),
        });
        setNotice(t('m22.deviceManagement.actionSuccess', { action }));
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [token, load],
  );

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m22.deviceManagement.loading')}</p>;
  return (
    <main aria-label="mobile-devices">
      <h1>{t('m22.deviceManagement.title')}</h1>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {rows.length === 0 ? (
        <p>{t('m22.deviceManagement.empty')}</p>
      ) : (
        <table aria-label="mobile-devices-table">
          <thead>
            <tr><th>{t('m22.deviceManagement.colDevice')}</th><th>{t('m22.deviceManagement.colPlatform')}</th><th>{t('m22.deviceManagement.colOs')}</th><th>{t('m22.deviceManagement.colApp')}</th><th>{t('m22.deviceManagement.colManufacturer')}</th><th>{t('m22.deviceManagement.colModel')}</th><th>{t('m22.deviceManagement.colStatus')}</th><th>{t('m22.deviceManagement.colLastSeen')}</th><th>{t('m22.deviceManagement.colActions')}</th></tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.deviceId}>
                <td>{d.deviceName ?? d.deviceIdentifier}</td>
                <td>{d.platform}</td>
                <td>{d.osVersion ?? '—'}</td>
                <td>{d.appVersion ?? '—'}</td>
                <td>{d.manufacturer ?? '—'}</td>
                <td>{d.model ?? '—'}</td>
                <td>{d.status}</td>
                <td>{at(d.lastSeenAt)}</td>
                <td>
                  <button
                    type="button"
                    aria-label={`approve-${d.deviceId}`}
                    disabled={busy !== null}
                    onClick={() => runAction(d.deviceId, 'approve')}
                  >
                    {t('m22.deviceManagement.approve')}
                  </button>
                  <button
                    type="button"
                    aria-label={`suspend-${d.deviceId}`}
                    disabled={busy !== null}
                    onClick={() => runAction(d.deviceId, 'suspend')}
                  >
                    {t('m22.deviceManagement.suspend')}
                  </button>
                  <button
                    type="button"
                    aria-label={`disable-${d.deviceId}`}
                    disabled={busy !== null}
                    onClick={() => runAction(d.deviceId, 'disable')}
                  >
                    {t('m22.deviceManagement.disable')}
                  </button>
                  <button
                    type="button"
                    aria-label={`revoke-${d.deviceId}`}
                    disabled={busy !== null}
                    onClick={() => runAction(d.deviceId, 'revoke')}
                  >
                    {t('m22.deviceManagement.revoke')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
